/**
 * @file Trakcja.tsx
 * @description "Trakcja" — portfolio board for internal apps and client engagements. Reads
 * GET /api/trakcja (projects + nested items + portfolio synthesis), polls every 30s. Opens
 * calm (statbar 🟢/🟡/🔴, the "lever of one morning" callout, "Czeka na Ciebie" decisions and
 * "Traci trakcję"), with the full roster split into Klienci / Moje apki as multi-open
 * expandable cards. Filters by momentum/type and sorts by traction/quietest/A-Z in-place
 * (no refetch). Blocker checkboxes resolve via PATCH; next-steps/blockers can be promoted to
 * real life-layer tasks via api.tasks.create ("Do Hermesa"). All copy is intentionally Polish
 * (personal instance convention). Open accordions survive the 30s poll (state lives outside
 * the fetched payload).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Inbox,
  Minus,
  Plus,
  Radar,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "../lib/api";
import type { NewLifeTaskFields, TrakcjaPayload, TrakcjaProject } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { TrakcjaProjectCard } from "../components/TrakcjaProjectCard";

const POLL_INTERVAL_MS = 30_000;

type MomentumFilter = "ALL" | "GREEN" | "YELLOW" | "RED";
type TypeFilter = "ALL" | "CLIENT" | "INTERNAL";
type SortMode = "traction" | "quietest" | "az";

const MOM_RANK: Record<TrakcjaProject["momentum"], number> = { GREEN: 0, YELLOW: 1, RED: 2 };

function relativeAgePl(iso: string | null): string {
  if (!iso) return "—";
  const ms = Math.max(0, Date.now() - new Date(iso).getTime());
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "przed chwilą";
  if (minutes < 60) return `${minutes} min temu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} godz. temu`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 dzień temu" : `${days} dni temu`;
}

function MetricTile({
  icon: Icon,
  label,
  value,
  tone,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tone: "ok" | "warn" | "neutral";
  active: boolean;
  onClick: () => void;
}) {
  const valueCls =
    tone === "ok" ? "text-emerald-400" : tone === "warn" ? "text-amber-400" : "text-gray-100";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`card p-4 flex items-center gap-3 text-left transition-colors hover:bg-surface-3/60 ${
        active ? "border-accent/50" : ""
      }`}
    >
      <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5 text-accent" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 truncate">
          {label}
        </p>
        <p className={`text-base font-semibold truncate ${valueCls}`}>{value}</p>
      </div>
    </button>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors ${
        active
          ? "bg-accent/15 text-accent border-accent/30"
          : "bg-surface-2 text-gray-400 border-border hover:text-gray-200 hover:bg-surface-3"
      }`}
    >
      {children}
    </button>
  );
}

export function Trakcja() {
  const [data, setData] = useState<TrakcjaPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<TypeFilter>("ALL");
  const [filterMomentum, setFilterMomentum] = useState<MomentumFilter>("ALL");
  const [sortMode, setSortMode] = useState<SortMode>("traction");
  const [calloutOpen, setCalloutOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const result = await api.trakcja.get();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const withBusy = useCallback(
    async (id: string, action: () => Promise<unknown>) => {
      setBusyId(id);
      setActionError(null);
      try {
        await action();
        await load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const onToggleExpand = useCallback((id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const onResolveItem = useCallback(
    (itemId: string) => withBusy(itemId, () => api.trakcja.resolveItem(itemId)),
    [withBusy]
  );

  const onAddToHermes = useCallback(
    (fields: NewLifeTaskFields, key: string) =>
      withBusy(key, async () => {
        await api.tasks.create(fields);
        setAddedKeys((prev) => new Set(prev).add(key));
      }),
    [withBusy]
  );

  const projects = data?.projects ?? [];
  const counts = useMemo(() => {
    const c = { GREEN: 0, YELLOW: 0, RED: 0 };
    for (const p of projects) c[p.momentum] += 1;
    return c;
  }, [projects]);

  const visible = useMemo(() => {
    const filtered = projects.filter(
      (p) =>
        (filterType === "ALL" || p.type === filterType) &&
        (filterMomentum === "ALL" || p.momentum === filterMomentum)
    );
    const sorted = [...filtered].sort((a, b) => {
      if (sortMode === "az") return a.name.localeCompare(b.name);
      if (sortMode === "quietest")
        return (a.last_activity_at ?? "").localeCompare(b.last_activity_at ?? "");
      const r = MOM_RANK[a.momentum] - MOM_RANK[b.momentum];
      if (r !== 0) return r;
      return (b.last_activity_at ?? "").localeCompare(a.last_activity_at ?? "");
    });
    return sorted;
  }, [projects, filterType, filterMomentum, sortMode]);

  const clients = visible.filter((p) => p.type === "CLIENT");
  const apps = visible.filter((p) => p.type === "INTERNAL");
  const portfolio = data?.portfolio ?? null;
  const decisions = portfolio?.needs_decision ?? [];
  const losing = portfolio?.losing_traction ?? [];

  const cardProps = {
    busyId,
    addedKeys,
    onToggleExpand,
    onResolveItem,
    onAddToHermes,
  };

  return (
    <div className="animate-fade-in space-y-8 max-w-3xl">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
            <Radar className="w-4.5 h-4.5 text-accent" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-100">Trakcja</h1>
            <p className="text-xs text-gray-500">
              Co goni i co czeka na Twoją decyzję
              {data?.generatedAt && (
                <span className="ml-2 font-mono text-gray-600">
                  · dane {relativeAgePl(data.generatedAt)}
                </span>
              )}
            </p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost flex-shrink-0" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Odśwież
        </button>
      </div>

      {/* Stat bar */}
      <div className="grid grid-cols-3 gap-4">
        <MetricTile
          icon={TrendingUp}
          label="Trzyma"
          value={String(counts.GREEN)}
          tone="ok"
          active={filterMomentum === "GREEN"}
          onClick={() => setFilterMomentum((m) => (m === "GREEN" ? "ALL" : "GREEN"))}
        />
        <MetricTile
          icon={Minus}
          label="Zwalnia"
          value={String(counts.YELLOW)}
          tone="warn"
          active={filterMomentum === "YELLOW"}
          onClick={() => setFilterMomentum((m) => (m === "YELLOW" ? "ALL" : "YELLOW"))}
        />
        <MetricTile
          icon={TrendingDown}
          label="Martwe"
          value={String(counts.RED)}
          tone="neutral"
          active={filterMomentum === "RED"}
          onClick={() => setFilterMomentum((m) => (m === "RED" ? "ALL" : "RED"))}
        />
      </div>

      {/* Lever callout */}
      {portfolio?.note && (
        <div className="card p-3 border-accent/25 bg-accent/5">
          <button
            type="button"
            onClick={() => setCalloutOpen((o) => !o)}
            className="w-full flex items-center gap-2 text-left"
          >
            <Zap className="w-4 h-4 text-accent flex-shrink-0" />
            <span className="text-xs font-medium text-accent">Lewar jednego poranka</span>
            {calloutOpen ? (
              <ChevronDown className="w-4 h-4 text-gray-500 ml-auto" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500 ml-auto" />
            )}
          </button>
          {calloutOpen && (
            <p className="mt-2 text-xs text-gray-300 leading-relaxed">{portfolio.note}</p>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <Pill active={filterType === "ALL"} onClick={() => setFilterType("ALL")}>
          Wszystko
        </Pill>
        <Pill active={filterType === "CLIENT"} onClick={() => setFilterType("CLIENT")}>
          Klienci
        </Pill>
        <Pill active={filterType === "INTERNAL"} onClick={() => setFilterType("INTERNAL")}>
          Apki
        </Pill>
        <span className="w-px h-5 bg-border mx-1" />
        <Pill active={sortMode === "traction"} onClick={() => setSortMode("traction")}>
          wg trakcji
        </Pill>
        <Pill active={sortMode === "quietest"} onClick={() => setSortMode("quietest")}>
          najdłużej cicho
        </Pill>
        <Pill active={sortMode === "az"} onClick={() => setSortMode("az")}>
          A–Z
        </Pill>
        {openIds.size > 0 && (
          <button
            type="button"
            onClick={() => setOpenIds(new Set())}
            className="ml-auto text-xs font-medium text-gray-400 hover:text-gray-200 px-2.5 py-1 rounded-lg border border-border hover:bg-surface-3 transition-colors"
          >
            Zwiń wszystko
          </button>
        )}
      </div>

      {/* Action error */}
      {actionError && (
        <div className="card p-3 flex items-center gap-3 border-amber-500/30 bg-amber-500/5">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-300 min-w-0 font-mono truncate">{actionError}</p>
        </div>
      )}

      {/* Load error */}
      {error && !data && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-gray-200">Nie udało się wczytać Trakcji.</p>
            <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse h-12 bg-surface-2 rounded-xl" />
          ))}
        </div>
      )}

      {/* Empty */}
      {data && projects.length === 0 && (
        <EmptyState
          icon={Radar}
          title="Brak danych Trakcji"
          description="Uruchom seed (server/scripts/seed-trakcja.js), aby wypełnić tablicę projektami."
        />
      )}

      {data && projects.length > 0 && (
        <>
          {/* Czeka na Ciebie */}
          {decisions.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Inbox className="w-4 h-4 text-amber-400" aria-hidden />
                <h2 className="text-sm font-semibold text-gray-100">Czeka na Ciebie</h2>
                <span className="text-[11px] text-gray-500">{decisions.length}</span>
              </div>
              <div className="card divide-y divide-border/50">
                {decisions.map((d, i) => {
                  const key = `decision:${i}`;
                  const added = addedKeys.has(key);
                  return (
                    <div key={key} className="flex items-start gap-2 p-2.5">
                      <span className="text-[10px] font-medium text-accent border border-accent/30 bg-accent/10 rounded px-1.5 py-0.5 flex-shrink-0 mt-0.5">
                        {d.project}
                      </span>
                      <p className="text-xs text-gray-300 min-w-0 flex-1">{d.what}</p>
                      <button
                        type="button"
                        onClick={() =>
                          onAddToHermes(
                            {
                              title: d.what,
                              notes: `Z Trakcji (decyzja): ${d.project}`,
                              priority: "MEDIUM",
                            },
                            key
                          )
                        }
                        disabled={added}
                        className={`flex-shrink-0 text-[10px] font-medium inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-colors disabled:opacity-70 ${
                          added
                            ? "text-emerald-400 border-emerald-500/30"
                            : "text-gray-400 border-border hover:text-gray-200 hover:bg-surface-3"
                        }`}
                      >
                        {added ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <Plus className="w-3 h-3" />
                        )}
                        {added ? "W Hermesie" : "Do Hermesa"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Traci trakcję */}
          {losing.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-4 h-4 text-red-400" aria-hidden />
                <h2 className="text-sm font-semibold text-gray-100">Traci trakcję</h2>
                <span className="text-[11px] text-gray-500">{losing.length}</span>
              </div>
              <ul className="card p-3 space-y-1.5">
                {losing.map((x, i) => (
                  <li key={i} className="text-xs text-gray-300 flex items-start gap-2">
                    <span className="text-red-400 mt-0.5 flex-shrink-0">•</span>
                    {x}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Klienci */}
          {clients.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-accent" aria-hidden />
                <h2 className="text-sm font-semibold text-gray-100">Klienci</h2>
                <span className="text-[11px] text-gray-500">{clients.length}</span>
              </div>
              <div className="space-y-2">
                {clients.map((p) => (
                  <TrakcjaProjectCard
                    key={p.id}
                    project={p}
                    expanded={openIds.has(p.id)}
                    {...cardProps}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Moje apki */}
          {apps.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Boxes className="w-4 h-4 text-accent" aria-hidden />
                <h2 className="text-sm font-semibold text-gray-100">Moje apki</h2>
                <span className="text-[11px] text-gray-500">{apps.length}</span>
              </div>
              <div className="space-y-2">
                {apps.map((p) => (
                  <TrakcjaProjectCard
                    key={p.id}
                    project={p}
                    expanded={openIds.has(p.id)}
                    {...cardProps}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
